import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useEntitlement } from "@/lib/entitlement";
import { Check, Sparkles } from "lucide-react";

const FEATURES = [
  "All 5 breed matches, ranked by fit",
  "Every reason we picked each one",
  "Full AUD cost breakdown & suburb detail",
  "Save unlimited breeds forever",
  "Priority AI advisor",
  "One-time payment — no subscription, no lock-in",
];

export default function Upgrade() {
  const { unlocked } = useEntitlement();
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const start = async () => {
    if (unlocked) { toast.info("You're already unlocked 🎉"); nav("/results"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/payments/checkout`,
        { lookup_key: "unlock_lifetime", origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error("Couldn't start checkout — please try again");
      setBusy(false);
    }
  };

  // Explicit button label — no nested ternaries.
  let btnLabel = "Unlock for $2";
  if (busy) btnLabel = "Redirecting…";
  else if (unlocked) btnLabel = "You're already unlocked";

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-12 pb-40 md:pb-24">
      <div className="text-center">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-bold uppercase tracking-wider">
          <Sparkles size={12} /> Fetchright Unlock
        </span>
        <h1 className="mt-6 font-display font-bold text-5xl md:text-6xl text-accent leading-[0.95]">
          Unlock every match.
        </h1>
        <p className="mt-6 text-lg text-muted-foreground max-w-xl mx-auto">
          One two-dollar payment. Every breed match, every cost breakdown, every save — yours forever. No subscription, no renewal, no catch.
        </p>
      </div>

      <div className="mt-12 rounded-[2rem] bg-accent text-accent-foreground p-10 md:p-14 warm-shadow">
        <div className="flex flex-wrap items-baseline gap-3">
          <span className="font-display font-bold text-6xl">$2</span>
          <span className="text-accent-foreground/70">AUD · one-time</span>
          <span className="ml-auto text-xs uppercase tracking-wider text-primary-foreground bg-primary rounded-full px-3 py-1 font-semibold">
            Lifetime access
          </span>
        </div>
        <ul className="mt-8 grid md:grid-cols-2 gap-x-6 gap-y-3">
          {FEATURES.map((f) => (
            <li key={f} className="flex gap-3 text-accent-foreground/90">
              <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground grid place-items-center shrink-0">
                <Check size={14} strokeWidth={3} />
              </span>
              {f}
            </li>
          ))}
        </ul>
        <button
          data-testid="upgrade-btn"
          onClick={start}
          disabled={busy}
          className="pill-btn mt-10 w-full md:w-auto inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-8 py-4 font-semibold text-lg disabled:opacity-60"
        >
          {btnLabel}
        </button>
        <p className="mt-4 text-xs text-accent-foreground/60">
          Secure checkout by Stripe. GST included where applicable. One-time payment — no subscription.
        </p>
        <p className="mt-2 text-[11px] text-accent-foreground/50 leading-relaxed">
          By paying you agree to our{" "}
          <Link to="/terms" className="underline hover:text-accent-foreground">Terms</Link>,{" "}
          <Link to="/privacy" className="underline hover:text-accent-foreground">Privacy</Link>{" "}
          and{" "}
          <Link to="/refunds" className="underline hover:text-accent-foreground">Refund policy</Link>.
        </p>
      </div>
    </div>
  );
}
